<script setup lang="ts">
import { onMounted, shallowRef } from 'vue';
import axios from 'axios';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { ApiError } from '@/utils/errors';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { useDialogManager } from '@/plugins/dialog-manager';
import TraderAdminApiSecretsRevealDialog from '@/modules/trader-admin/components/TraderAdminApiSecretsRevealDialog.vue';

interface ApiCredential {
  id: string;
  clientId: string;
  lastUsedAt: string | null;
}

const isLoading = shallowRef(false);
const showDialog = shallowRef(false);
const createdCredentials = shallowRef<{
  clientId: string;
  clientSecret: string;
} | null>(null);
const isCreating = shallowRef(false);
const existingCredentials = shallowRef<ApiCredential[]>([]);

const snackbarManager = useSnackbarManager();
const dialogManager = useDialogManager();

async function loadData(): Promise<void> {
  isLoading.value = true;

  try {
    const { data } = await axios.get<{
      items: ApiCredential[];
    }>('/api/1/trader-admin/api-credentials');

    existingCredentials.value = data.items;
  } catch (err) {
    snackbarManager.show({
      message: new ApiError(i18nServerMessage(err as Error)).message,
      color: 'error',
    });
  } finally {
    isLoading.value = false;
  }
}

function createApiSecret(): void {
  dialogManager.confirm({
    title: 'Create New API Keys?',
    message:
      'This will generate a new client_id and client_secret pair. The secret will only be shown once, so be ready to save in a secure way.',
    isRejectable: true,
    rejectText: 'Cancel',
    acceptText: 'Create',
    onAccept: () => confirmCreateApiSecret(),
  });
}

async function confirmCreateApiSecret(): Promise<void> {
  if (isCreating.value) return;

  isCreating.value = true;

  try {
    const { data } = await axios.post<{
      clientId: string;
      secret: string;
    }>('/api/1/trader-admin/api-credentials');

    createdCredentials.value = {
      clientId: data.clientId,
      clientSecret: data.secret,
    };

    showDialog.value = true;
  } catch (err) {
    snackbarManager.show({
      message: new ApiError(i18nServerMessage(err as Error)).message,
      color: 'error',
    });
  } finally {
    isCreating.value = false;
  }
}

function handleDialogClose(): void {
  showDialog.value = false;
  void loadData();
  // refresh the list after creating new credentials
  createdCredentials.value = null;
}

async function copyClientIdToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
  snackbarManager.show({
    message: `Client ID copied to clipboard`,
    color: 'success',
  });
}

function deleteApiSecret(credential: ApiCredential): void {
  dialogManager.alert({
    title: 'Are you sure?',
    message: `This will permanently delete the API credentials with Client ID: ${credential.clientId}`,
    isRejectable: true,
    rejectText: 'Cancel',
    acceptText: 'Delete',
    onAccept: () => confirmDeleteApiSecret(credential.id),
    shouldManuallyConfirm: true,
  });
}

async function confirmDeleteApiSecret(credentialId: string): Promise<void> {
  try {
    await axios.delete(`/api/1/trader-admin/api-credentials/${credentialId}`);
    snackbarManager.show({
      message: 'API credentials deleted successfully',
      color: 'success',
    });
    await loadData(); // Refresh the list
  } catch (err) {
    snackbarManager.show({
      message: new ApiError(i18nServerMessage(err as Error)).message,
      color: 'error',
    });
  }
}

onMounted(() => {
  void loadData();
});
</script>

<template>
  <VCard class="flex d-flex flex-column">
    <VCardTitle class="mb-4">
      <span class="headline">API Keys</span>
    </VCardTitle>

    <VCardText class="text--primary">
      <div v-if="isLoading" class="text-center">
        <VProgressCircular color="primary" indeterminate />
        <div class="mt-2">Loading API secrets...</div>
      </div>

      <template v-else>
        <template v-if="existingCredentials.length > 0">
          <p class="mt-n4 mb-8">
            To enforce secuirity best practices, we don't show the
            <strong>client_secret</strong> here. If lost, generate a new
            <strong>client_secret/client_id</strong> pair.
          </p>
          <div class="mb-4">
            <VRow>
              <VCol v-for="credential in existingCredentials" :key="credential.id" cols="12" md="6">
                <VCard outlined>
                  <VCardText>
                    <div class="d-flex align-center mb-2">
                      <VIcon class="mr-2" color="primary">mdi-key</VIcon>
                      <span class="text-subtitle-1 font-weight-medium">Client ID</span>
                    </div>
                    <div class="d-flex align-center mb-3">
                      <VTextField
                        class="flex-grow-1 mr-2"
                        dense
                        hide-details
                        readonly
                        :value="credential.clientId"
                      />
                      <div class="d-flex">
                        <VTooltip bottom open-delay="300">
                          <template #activator="{ on, attrs }">
                            <VBtn
                              class="icon"
                              icon
                              small
                              v-bind="attrs"
                              v-on="on"
                              @click="copyClientIdToClipboard(credential.clientId)"
                            >
                              <VIcon small>mdi-content-copy</VIcon>
                            </VBtn>
                          </template>
                          <span>Copy Client ID</span>
                        </VTooltip>
                        <VTooltip bottom open-delay="300">
                          <template #activator="{ on, attrs }">
                            <VBtn
                              class="icon"
                              icon
                              small
                              v-bind="attrs"
                              v-on="on"
                              @click="deleteApiSecret(credential)"
                            >
                              <VIcon small>mdi-delete</VIcon>
                            </VBtn>
                          </template>
                          <span>Delete API Key</span>
                        </VTooltip>
                      </div>
                    </div>
                    <div class="text-caption grey--text">
                      <VIcon class="mr-1" small>mdi-clock-outline</VIcon>
                      Last used:
                      {{
                        credential.lastUsedAt
                          ? new Date(credential.lastUsedAt).toLocaleString()
                          : 'Never'
                      }}
                    </div>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </div>

          <div class="text-right mb-4 mr-2 mt-8">
            <VBtn color="primary" :disabled="isCreating" @click="createApiSecret()">
              <VProgressCircular v-if="isCreating" class="mr-2" indeterminate size="20" />
              Create New API Keys
            </VBtn>
          </div>
        </template>

        <template v-else>
          <div class="text-center pa-4 mt-n12">
            <div class="mb-4">
              <VIcon color="grey lighten-1" size="64">mdi-key-variant</VIcon>
            </div>
            <p class="text-h6 mb-2">API Keys Management</p>
            <p class="text-body-2 grey--text">
              Create and manage <strong>client_id</strong> and <strong>client_secret</strong> pairs
              for API access.
            </p>
            <VBtn class="mt-4" color="primary" :disabled="isCreating" @click="createApiSecret()">
              <VProgressCircular v-if="isCreating" class="mr-2" indeterminate size="20" />
              Create New API Key
            </VBtn>
          </div>
        </template>
      </template>
    </VCardText>

    <TraderAdminApiSecretsRevealDialog
      :credentials="createdCredentials"
      :value="showDialog"
      @update:value="handleDialogClose"
    />
  </VCard>
</template>

<style lang="scss" scoped>
.icon {
  opacity: 0.7;
}
.icon:hover {
  opacity: 1;
}
</style>
